import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Check as CheckIcon,
  Cancel as CancelIcon,
  Upgrade as UpgradeIcon,
  Settings as SettingsIcon,
  Api as ApiIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';

const SubscriptionManagement: React.FC = () => {
  const navigate = useNavigate();
  const { subscription, cancelSubscription, hasFeature, getRemainingReels } = useSubscription();
  const [cancelDialog, setCancelDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!subscription) {
    return (
      <Container maxWidth="md">
        <Box py={8} textAlign="center">
          <Typography variant="h4" gutterBottom>
            No Active Subscription
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You don't have an active subscription. Choose a plan to get started.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/pricing')}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            View Plans
          </Button>
        </Box>
      </Container>
    );
  }

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      await cancelSubscription();
      setCancelDialog(false);
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const usagePercentage = subscription.plan.reelsLimit === -1 
    ? 0 
    : (subscription.reelsUsedThisMonth / subscription.plan.reelsLimit) * 100;

  return (
    <Container maxWidth="md">
      <Box py={8}>
        {/* Header */}
        <Box textAlign="center" mb={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Subscription Management
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Manage your AutoReelr subscription and billing
            </Typography>
          </motion.div>
        </Box>

        {/* Current Plan */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="between" mb={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <StarIcon color="primary" sx={{ fontSize: 32 }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {subscription.plan.name} Plan
                    {subscription.hasByoaAddon && (
                      <Chip label="BYOA" color="secondary" size="small" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active until {subscription.currentPeriodEnd.toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={subscription.isActive ? 'Active' : 'Inactive'}
                color={subscription.isActive ? 'success' : 'error'}
                icon={subscription.isActive ? <CheckIcon /> : <CancelIcon />}
              />
            </Box>

            {/* Usage */}
            <Box mb={3}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">
                  Reels Used This Month
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {subscription.plan.reelsLimit === -1 
                    ? `${subscription.reelsUsedThisMonth} / Unlimited`
                    : `${subscription.reelsUsedThisMonth} / ${subscription.plan.reelsLimit}`
                  }
                </Typography>
              </Box>
              {subscription.plan.reelsLimit !== -1 && (
                <LinearProgress
                  variant="determinate"
                  value={Math.min(usagePercentage, 100)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: usagePercentage > 90 
                        ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    },
                  }}
                />
              )}
            </Box>

            {/* Plan Features */}
            <Box mb={3}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Your Plan Includes:
              </Typography>
              <Grid container spacing={1}>
                {[
                  subscription.plan.reelsLimit === -1 ? 'Unlimited reels' : `${subscription.plan.reelsLimit} reels per month`,
                  subscription.plan.hasAnalytics && 'Advanced analytics',
                  subscription.plan.hasAdvancedScheduling && 'Advanced scheduling',
                  subscription.plan.has4KExport && '4K video export',
                  subscription.plan.hasPrioritySupport && 'Priority support',
                  subscription.plan.hasTeamCollaboration && 'Team collaboration',
                  subscription.plan.hasWhiteLabel && 'White-label solution',
                  subscription.hasByoaAddon && 'Bring Your Own API keys',
                ].filter(Boolean).map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CheckIcon color="primary" fontSize="small" />
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Actions */}
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<UpgradeIcon />}
                onClick={() => navigate('/pricing')}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                {subscription.plan.type === 'free' ? 'Upgrade Plan' : 'Change Plan'}
              </Button>
              
              {subscription.hasByoaAddon && (
                <Button
                  variant="outlined"
                  startIcon={<ApiIcon />}
                  onClick={() => navigate('/settings')}
                >
                  Manage API Keys
                </Button>
              )}
              
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => window.open('https://billing.stripe.com', '_blank')}
              >
                Billing Portal
              </Button>
              
              {subscription.plan.type !== 'free' && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setCancelDialog(true)}
                >
                  Cancel Subscription
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* BYOA Option */}
        {subscription.plan.hasByoaOption && !subscription.hasByoaAddon && (
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              Want to save $10/month?
            </Typography>
            <Typography variant="body2" paragraph>
              Add the Bring Your Own API (BYOA) option to your plan and manage your own API keys for Pexels, Segmind, and other services.
            </Typography>
            <Button size="small" variant="outlined">
              Add BYOA Option
            </Button>
          </Alert>
        )}

        {/* Cancel Dialog */}
        <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Cancel Subscription</DialogTitle>
          <DialogContent>
            <Typography paragraph>
              Are you sure you want to cancel your subscription? You'll lose access to:
            </Typography>
            <List dense>
              {[
                'Monthly reel allowance',
                'Advanced features',
                'Priority support',
                'API integrations',
              ].map((item, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemIcon>
                    <CancelIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
            <Typography variant="body2" color="text.secondary">
              Your subscription will remain active until {subscription.currentPeriodEnd.toLocaleDateString()}.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialog(false)}>
              Keep Subscription
            </Button>
            <Button
              onClick={handleCancelSubscription}
              color="error"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Canceling...' : 'Cancel Subscription'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default SubscriptionManagement;